/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import axios from "axios";
import baseUrl from "..";
import { authToken, authUserID, axiosInstance } from "../axiosConfig";


// Fetches container inventory statistics for dashboard cards
const getContainerInventoryStats = async () => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/container_inventory/statistics`,
      {
        headers: {
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data;
    }
  }
};


// Fetches container list for inventory
const getContainerList = async (page: number, size: number, sort: any, order: any, keyword: any, lot?: string, mediaType?: string, fromDate?: string, toDate?: string) => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/container_inventory/list?page=${page}&size=${size}&sort=${sort}&order=${order}${keyword ? `&keyword=${keyword}` : ''}${lot ? `&batch_name=${lot}` : ''}${mediaType ? `&media_name=${mediaType}` : ''}${fromDate ? `&from_date=${fromDate}` : ''}${toDate ? `&to_date=${toDate}` : ''}`,
      {
        headers: {
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data;
    }
  }
};



const listContainerPlateTypes =  async () => {
    try {
      const response = await axiosInstance.get(`${baseUrl}/media/list_container_type_masters?page=1&size=1000&sort=id&order=desc&id&name&description&status&keyword` ,
      {
        headers: {
          'x-auth-token': authToken,
          'x-userid': authUserID
        }
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response?.data
      }
    }
}


  // Fetches batch codes for the filter dropdown
 const getContainerBatchCodes = async () => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/container_inventory/list?page=1&size=1000&sort=id&order=desc`,
      {
        headers: {
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data;
    }
  }
};



// Fetches container batch list for details view
const getContainerBatch = async (batchId: string) => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/container_inventory/list?page=1&size=50&sort=id&order=desc&id=${batchId}`,
      {
        headers: {
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data;
    }
  }
};


// Fetches container batch details for details view
const getContainerBatchDetails = async (batchId: string) => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/container_inventory/list_container_batch_lot_details?container_batch_id=${batchId}`,
      {
        headers: {
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data;
    }
  }
};


// Fetches container details for a selected lot
const getContainerLotContainerDetails = async (
  lotId: string,
  batchId: string
) => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/container_inventory/list_container_batch_lot_details?lot_id=${lotId}&container_batch_id=${batchId}`,
      {
        headers: {
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching container lot container details", error);
    throw error;
  }
};


// Container Type Dropdown
const getContainerTypes = async () => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/media/list_container_type_masters?page=1&size=50&sort=id&order=desc`,
      {
        headers: {
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data;
    }
  }
};


// Capacity Dropdown based on Container Type
const getContainerCapacities = async (containerTypeId: string | number) => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/media/list_container_capacity_mappings?page=1&size=50&sort=id&order=desc&container_type_id=${containerTypeId}`,
      {
        headers: {
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data;
    }
  }
};


// Save Container Inventory
const createContainerInventory = async (payload: any) => {
  try {
    const response = await axiosInstance.post(
      `${baseUrl}/container_inventory/save`,
      payload,
      {
        headers: {
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data;
    }
  }
};


// Fetch individual container details
const getIndividualContainerDetails = async (containerId: string) => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/container_inventory/list_container_batch_lot_details?container_id=${containerId}`,
      {
        headers: {
          "x-auth-token": authToken,
          "x-userid": authUserID,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data;
    }
  }
};



export const ContainerInventoryService = {
  getContainerInventoryStats,
  getContainerList,
  listContainerPlateTypes,
  getContainerBatchCodes,
  getContainerBatch,
  getContainerBatchDetails,
  getContainerLotContainerDetails,
  getContainerTypes,
  getContainerCapacities,
  createContainerInventory,
  getIndividualContainerDetails,
};